import { useState, useEffect, useMemo } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Users,
  Search,
  DollarSign,
  Download,
  AlertCircle,
  RefreshCw,
  UserPlus,
  Users2,
} from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import { PartnerPortalService, type PartnerReferral } from '@/domain/partner-portal'

export function AffiliateReferrals() {
  const [l1Members, setL1Members] = useState<PartnerReferral[]>([])
  const [l2Members, setL2Members] = useState<PartnerReferral[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState('L1')
  const [l1Count, setL1Count] = useState(0)
  const [l2Count, setL2Count] = useState(0)

  const fetchReferrals = async (showRefresh = false) => {
    if (showRefresh) setIsRefreshing(true)
    else setIsLoading(true)
    setError(null)

    try {
      const result = await PartnerPortalService.getReferrals({
        level: 'all',
        limit: 100
      })

      if (result.success && result.data) {
        setL1Members(result.data.l1_members || [])
        setL2Members(result.data.l2_members || [])
        setL1Count(result.data.l1_count || 0)
        setL2Count(result.data.l2_count || 0)
      } else {
        setError(result.error || 'ไม่สามารถโหลดข้อมูลได้')
      }
    } catch (err: any) {
      setError(err.message || 'เกิดข้อผิดพลาด')
    } finally {
      setIsLoading(false)
      setIsRefreshing(false)
    }
  }

  useEffect(() => {
    fetchReferrals()
  }, [])

  // Filter members by search
  const filteredL1 = useMemo(() => {
    if (!searchQuery) return l1Members
    return l1Members.filter(m => 
      m.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.firstname?.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [l1Members, searchQuery])

  const filteredL2 = useMemo(() => {
    if (!searchQuery) return l2Members
    return l2Members.filter(m => 
      m.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.firstname?.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [l2Members, searchQuery])

  // Stats
  const stats = useMemo(() => ({
    totalL1: l1Count,
    totalL2: l2Count,
    total: l1Count + l2Count,
    totalCommission: [...l1Members, ...l2Members].reduce((acc, m) => {
      const commission = Number(m.commission_earned) || 0
      return acc + commission
    }, 0),
  }), [l1Count, l2Count, l1Members, l2Members])

  const getLevelBadge = (level: 'L1' | 'L2') => {
    const config = {
      L1: { label: 'L1', className: 'bg-blue-500/10 text-blue-500' },
      L2: { label: 'L2', className: 'bg-purple-500/10 text-purple-500' },
    }
    return <Badge variant="secondary" className={cn("border-0", config[level].className)}>{config[level].label}</Badge>
  }

  const maskUsername = (username: string) => {
    if (!username || username.length <= 4) return username
    return username.slice(0, 4) + '****' + username.slice(-3)
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-16 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <p className="text-lg font-medium mb-2">ไม่สามารถโหลดข้อมูลได้</p>
        <p className="text-sm text-muted-foreground mb-4">{error}</p>
        <Button onClick={() => fetchReferrals()}>ลองใหม่</Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">สมาชิกที่แนะนำ</h1>
          <p className="text-muted-foreground">รายการสมาชิกที่คุณแนะนำมา (L1 และ L2)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => fetchReferrals(true)} disabled={isRefreshing}>
            <RefreshCw className={cn("h-4 w-4 mr-2", isRefreshing && "animate-spin")} />
            รีเฟรช
          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            ส่งออก
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-primary/10">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">สมาชิกทั้งหมด</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-blue-500/10">
                <UserPlus className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">L1 (แนะนำตรง)</p>
                <p className="text-2xl font-bold text-blue-500">{stats.totalL1}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-purple-500/10">
                <Users2 className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">L2 (ชั้น 2)</p>
                <p className="text-2xl font-bold text-purple-500">{stats.totalL2}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-amber-500/10">
                <DollarSign className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">คอมมิชชั่นรวม</p>
                <p className="text-2xl font-bold text-amber-500">{formatCurrency(stats.totalCommission)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="ค้นหาสมาชิก..." 
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Tabs for L1/L2 */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="L1" className="gap-2">
            <UserPlus className="h-4 w-4" />
            L1 - แนะนำตรง ({filteredL1.length})
          </TabsTrigger>
          <TabsTrigger value="L2" className="gap-2">
            <Users2 className="h-4 w-4" />
            L2 - ชั้น 2 ({filteredL2.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="L1" className="mt-4">
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>สมาชิก</TableHead>
                  <TableHead>ระดับ</TableHead>
                  <TableHead>วันที่สมัคร</TableHead>
                  <TableHead className="text-right">ยอด WinLose</TableHead>
                  <TableHead className="text-right">คอมมิชชั่นที่ได้</TableHead>
                  <TableHead>ใช้งานล่าสุด</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredL1.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      ไม่พบข้อมูลสมาชิก L1
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredL1.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell className="font-medium">
                        {maskUsername(member.username)}
                        {member.firstname && (
                          <span className="text-xs text-muted-foreground ml-2">
                            ({member.firstname})
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{getLevelBadge('L1')}</TableCell>
                      <TableCell className="text-muted-foreground">{member.joined_at || '-'}</TableCell>
                      <TableCell className="text-right font-medium">
                        {member.total_winlose != null ? formatCurrency(Number(member.total_winlose) || 0) : '-'}
                      </TableCell>
                      <TableCell className="text-right font-medium text-win">
                        {member.commission_earned != null ? formatCurrency(Number(member.commission_earned) || 0) : '-'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{member.last_active || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="L2" className="mt-4">
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>สมาชิก</TableHead>
                  <TableHead>ระดับ</TableHead>
                  <TableHead>วันที่สมัคร</TableHead>
                  <TableHead className="text-right">ยอด WinLose</TableHead>
                  <TableHead className="text-right">คอมมิชชั่นที่ได้</TableHead>
                  <TableHead>ใช้งานล่าสุด</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredL2.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      ไม่พบข้อมูลสมาชิก L2
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredL2.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell className="font-medium">
                        {maskUsername(member.username)}
                        {member.firstname && (
                          <span className="text-xs text-muted-foreground ml-2">
                            ({member.firstname})
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{getLevelBadge('L2')}</TableCell>
                      <TableCell className="text-muted-foreground">{member.joined_at || '-'}</TableCell>
                      <TableCell className="text-right font-medium">
                        {member.total_winlose != null ? formatCurrency(Number(member.total_winlose) || 0) : '-'}
                      </TableCell>
                      <TableCell className="text-right font-medium text-win">
                        {member.commission_earned != null ? formatCurrency(Number(member.commission_earned) || 0) : '-'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{member.last_active || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Pagination Info */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          แสดง {activeTab === 'L1' ? filteredL1.length : filteredL2.length} รายการ
        </p>
      </div>
    </div>
  )
}
